import React, { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { formatApiError } from "@/lib/utils-app";
import { ShareNetworkIcon as ShareNetwork, CopyIcon as Copy, ArrowSquareOutIcon as ArrowSquareOut, InstagramLogoIcon as InstagramLogo, FacebookLogoIcon as FacebookLogo, TwitterLogoIcon as TwitterLogo, EnvelopeSimpleIcon as EnvelopeSimple, QrCodeIcon as QrCode, UsersFourIcon as UsersFour } from "@phosphor-icons/react";
import ReferralCreditsCard from "@/components/ReferralCreditsCard";
import { isNativeApp, webOrigin } from "@/lib/nativeBridge";

/**
 * Share-my-kennel toolkit — pre-formatted social copy + QR + referral
 * link the breeder can email to other breeders.
 */
export default function ShareKennelPage() {
  const [toolkit, setToolkit] = useState(null);
  const [referral, setReferral] = useState(null);
  const [refStats, setRefStats] = useState(null);

  const copy = (text, label) => {
    navigator.clipboard.writeText(text).then(
      () => toast.success(`${label} copied to clipboard`),
      () => toast.error("Copy failed — please select and copy manually")
    );
  };

  useEffect(() => {
    api.get("/breeder-onboarding/share-toolkit").then((r) => setToolkit(r.data)).catch((e) => {
      toast.error(formatApiError(e));
    });
    api.post("/breeder-onboarding/referral-link").then((r) => setReferral(r.data)).catch(() => {});
    api.get("/breeder-onboarding/referral-link/stats").then((r) => setRefStats(r.data)).catch(() => {});
  }, []);

  const refUrl = referral?.code
    ? `${webOrigin()}/register?ref=${referral.code}`
    : null;

  return (
    <AppShell>
      <section className="max-w-3xl mx-auto px-4 sm:px-6 py-8 space-y-6" data-testid="share-kennel-page">
        <header>
          <h1 className="font-spectral text-3xl sm:text-4xl tracking-tight">Share your kennel</h1>
          <p className="text-[#6B6B6B] mt-3 leading-relaxed">
            {isNativeApp()
              ? "Drive your existing buyers and contacts to your Armstrong K9 page."
              : "Drive your existing buyers and contacts to your Armstrong K9 page — and earn referral credit when other breeders sign up through you."}
          </p>
        </header>

        {/* Referral credits ledger — only renders when user has earned at least one credit */}
        <ReferralCreditsCard />

        {/* Public URL */}
        {toolkit?.public_url && (
          <div className="rounded-xl border border-[#EAEAEA] bg-white p-5" data-testid="share-public-url">
            <h2 className="font-spectral text-lg mb-2 flex items-center gap-2"><ShareNetwork size={18} weight="duotone" /> Your public kennel URL</h2>
            <div className="flex items-center gap-2 flex-wrap">
              <code className="text-[13px] bg-[#FBFBFB] border border-[#EAEAEA] rounded-lg px-3 py-2 font-mono break-all flex-1 min-w-[200px]">{toolkit.public_url}</code>
              <Button size="sm" variant="outline" onClick={() => copy(toolkit.public_url, "Kennel URL")} className="rounded-full" data-testid="share-copy-url">
                <Copy size={13} weight="bold" /> Copy
              </Button>
              <Button asChild size="sm" variant="outline" className="rounded-full">
                <a href={toolkit.public_url} target="_blank" rel="noreferrer">
                  Open <ArrowSquareOut size={11} />
                </a>
              </Button>
            </div>
          </div>
        )}

        {/* QR */}
        {toolkit && (
          <div className="rounded-xl border border-[#EAEAEA] bg-white p-5" data-testid="share-qr">
            <h2 className="font-spectral text-lg mb-2 flex items-center gap-2"><QrCode size={18} weight="duotone" /> QR code (great for trade-show banners, business cards)</h2>
            <div className="flex items-center gap-4 mt-3">
              <img
                src="/api/breeder-onboarding/share-toolkit/qr.svg"
                alt="QR to public kennel page"
                className="h-32 w-32 border border-[#EAEAEA] rounded-lg bg-white"
                data-testid="share-qr-img"
              />
              <div>
                <p className="text-[13px] text-[#3F3F3F]">Scan to open your public kennel page.</p>
                <Button asChild size="sm" variant="outline" className="rounded-full mt-2">
                  <a href="/api/breeder-onboarding/share-toolkit/qr.svg" download="armstrong-k9-kennel-qr.svg" data-testid="share-qr-download">
                    Download SVG
                  </a>
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Pre-formatted templates */}
        {toolkit?.templates && (
          <div className="rounded-xl border border-[#EAEAEA] bg-white p-5" data-testid="share-templates">
            <h2 className="font-spectral text-lg mb-3">One-click social copy</h2>
            <div className="space-y-3">
              <TemplateBlock icon={InstagramLogo} label="Instagram" text={toolkit.templates.instagram} onCopy={copy} testIdPrefix="share-tpl-instagram" />
              <TemplateBlock icon={FacebookLogo}  label="Facebook"  text={toolkit.templates.facebook}  onCopy={copy} testIdPrefix="share-tpl-facebook" />
              <TemplateBlock icon={TwitterLogo}   label="X / Twitter" text={toolkit.templates.twitter}  onCopy={copy} testIdPrefix="share-tpl-twitter" />
              <TemplateBlock icon={EnvelopeSimple} label="Email"     text={toolkit.templates.email}     onCopy={copy} testIdPrefix="share-tpl-email" />
            </div>
          </div>
        )}

        {/* Referral link */}
        <div className="rounded-xl border-2 border-gold/40 bg-[#FBF7EC] p-5" data-testid="share-referral">
          <h2 className="font-spectral text-lg mb-2 flex items-center gap-2"><UsersFour size={18} weight="duotone" /> Invite another breeder</h2>
          <p className="text-[13px] text-[#3F3F3F] leading-relaxed">
            {isNativeApp()
              ? "Send a friend or fellow breeder this magic link — they'll sign up with you pre-attributed as their referrer."
              : "Send a friend or fellow breeder this magic link — they'll sign up with you pre-attributed as their referrer. We track every signup so you can claim referral credits when we roll out the rewards program."}
          </p>
          {refUrl && (
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <code className="text-[13px] bg-white border border-[#E8D9A6] rounded-lg px-3 py-2 font-mono break-all flex-1 min-w-[200px]" data-testid="share-referral-url">{refUrl}</code>
              <Button size="sm" onClick={() => copy(refUrl, "Referral link")} className="rounded-full bg-ink hover:bg-crimson text-white" data-testid="share-referral-copy">
                <Copy size={13} weight="bold" /> Copy link
              </Button>
            </div>
          )}
          {refStats && (
            <p className="mt-3 text-[12px] text-[#8A6A00]">
              <strong>{refStats.uses}</strong> breeder{refStats.uses === 1 ? " has" : "s have"} signed up using your link.
            </p>
          )}
        </div>
      </section>
    </AppShell>
  );
}

function TemplateBlock({ icon: Ic, label, text, onCopy, testIdPrefix }) {
  return (
    <div className="rounded-lg border border-[#EAEAEA] bg-[#FBFBFB] p-3" data-testid={testIdPrefix}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2">
          <Ic size={16} weight="duotone" className="text-gold" />
          <span className="text-[12.5px] font-bold uppercase tracking-wider">{label}</span>
        </div>
        <Button size="sm" variant="outline" onClick={() => onCopy(text, label)} className="rounded-full" data-testid={`${testIdPrefix}-copy`}>
          <Copy size={11} weight="bold" /> Copy
        </Button>
      </div>
      <pre className="text-[12px] text-[#3F3F3F] whitespace-pre-wrap font-sans leading-snug">{text}</pre>
    </div>
  );
}
